import { useState, useEffect } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { X, Download, Loader2, FileText } from "lucide-react";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const firstOfMonthISO = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Customer or vendor statement modal.
 * Props:
 *  - open, onClose
 *  - kind: "customer" | "vendor"
 *  - bookId
 *  - party: customer or vendor object (must have id + name)
 *  - lang
 */
const StatementModal = ({ open, onClose, kind, bookId, party, lang }) => {
  const [start, setStart] = useState(firstOfMonthISO());
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const segment = kind === "customer" ? "customers" : "vendors";

  const load = async () => {
    if (!party?.id) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/back-office/books/${bookId}/${segment}/${party.id}/statement`, { params: { start, end } });
      setData(d);
    } catch (err) { toast.error("Could not load statement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, party?.id]);

  const downloadPdf = async () => {
    try {
      const { data: blob } = await api.get(`/back-office/books/${bookId}/${segment}/${party.id}/statement.pdf`, { params: { start, end }, responseType: "blob" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement-${(party.name || party.id).replace(/\s+/g, "_")}-${end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    } catch { toast.error("PDF download failed"); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={`statement-${kind}-modal`}>
      <div className="bg-white max-w-3xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div>
            <div className="rn3-eyebrow">{lang === "es" ? "Estado de Cuenta" : "Statement of Activity"}</div>
            <h3 className="font-serif-display text-2xl mt-1">{party?.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Desde" : "Start"}</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rn3-input" data-testid={`stmt-${kind}-start`} />
            </div>
            <div>
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Hasta" : "End"}</label>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rn3-input" data-testid={`stmt-${kind}-end`} />
            </div>
            <button type="button" onClick={load} className="rn3-btn-gold" data-testid={`stmt-${kind}-refresh`}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {lang === "es" ? "Generar" : "Refresh"}
            </button>
            <div className="ml-auto">
              <button type="button" onClick={downloadPdf} className="rn3-btn-primary" data-testid={`stmt-${kind}-download`}>
                <Download className="w-4 h-4" /> {lang === "es" ? "Descargar PDF" : "Download PDF"}
              </button>
            </div>
          </div>

          {data && (
            <div className="border border-[#E5E5E5] overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid={`stmt-${kind}-table`}>
                <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
                  <tr>
                    <th className="py-2 px-3">{lang === "es" ? "Fecha" : "Date"}</th>
                    <th className="py-2 px-3">{lang === "es" ? "Tipo" : "Type"}</th>
                    <th className="py-2 px-3">{lang === "es" ? "Referencia" : "Reference"}</th>
                    <th className="py-2 px-3 text-right">{kind === "customer" ? (lang === "es" ? "Cargos" : "Charges") : (lang === "es" ? "Facturas" : "Bills")}</th>
                    <th className="py-2 px-3 text-right">{lang === "es" ? "Pagos" : "Payments"}</th>
                    <th className="py-2 px-3 text-right">{lang === "es" ? "Saldo" : "Balance"}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#F3EFE9] bg-[#FDFBF7]">
                    <td className="py-2 px-3 text-xs text-[#737373]">—</td>
                    <td className="py-2 px-3 italic text-[#737373]">{lang === "es" ? "Saldo inicial" : "Opening balance"}</td>
                    <td className="py-2 px-3"></td>
                    <td className="py-2 px-3"></td>
                    <td className="py-2 px-3"></td>
                    <td className="py-2 px-3 text-right tabular-nums">{fmt(data.opening_balance)}</td>
                  </tr>
                  {data.rows.length === 0 && (
                    <tr><td colSpan="6" className="py-8 text-center text-[#737373]">{lang === "es" ? "Sin actividad en este período." : "No activity in this period."}</td></tr>
                  )}
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-[#F3EFE9]" data-testid={`stmt-${kind}-row-${i}`}>
                      <td className="py-2 px-3 whitespace-nowrap">{r.date}</td>
                      <td className="py-2 px-3">{r.kind}</td>
                      <td className="py-2 px-3 text-[#737373]">{r.ref}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{r.debit ? fmt(r.debit) : ""}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-[#15803D]">{r.credit ? fmt(r.credit) : ""}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(r.balance)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#C4A252] bg-[#FDFBF7]">
                    <td className="py-3 px-3"></td>
                    <td className="py-3 px-3 font-medium">{lang === "es" ? "Totales del período" : "Period totals"}</td>
                    <td className="py-3 px-3"></td>
                    <td className="py-3 px-3 text-right tabular-nums font-medium">{fmt(data.totals.debits)}</td>
                    <td className="py-3 px-3 text-right tabular-nums font-medium text-[#15803D]">{fmt(data.totals.credits)}</td>
                    <td className={`py-3 px-3 text-right tabular-nums font-serif-display text-lg ${data.totals.closing_balance > 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(data.totals.closing_balance)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatementModal;
